import React, { useEffect, useMemo, useState } from 'react'
import s from './index.module.scss'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { ReactComponent as ArrowIcon } from '../../../assets/icons/arrow-down.svg'
import { ReactComponent as Hamburger } from '../../../assets/icons/hamburger.svg'
import { navLinks } from '../../../constants/nav'
import Header from '../../Header'
import { socket, useGetSidebarQuery, useLazySubscribeToEventsQuery } from '../../../store/api'
import TrucksFillModal from '../../../pages/Trucks/TrucksFillModal'
import { useAppSelector } from '../../../hooks/useAppSelector'
import TrucksNotificationModal from '../../../pages/Trucks/TrucksNotificationModal'
import OrdersDetailModal from '../../../pages/Orders/OrdersDetailModal'
import { useActions } from '../../../hooks/useActions'
import LegalPersonsDetailModal from '../../../pages/LegalPersons/LegalPersonsDetailModal'
import NaturalPersonsDetailModal from '../../../pages/NaturalPersons/NaturalPersonsDetailModal'
import { useGetCurrentUserQuery } from '../../../store/admins/admins.api'
import Loader from '../../UI/Loader'
import Button from '../../UI/Button'
import { useSignOutMutation } from '../../../store/auth/auth.api'
import RefuellingsDetailModal from '../../../pages/Refuellings/RefuellingsDetailModal'
import EditPartiesModal from '../../../pages/Parties/EditPartiesModal'

interface IProps {
	titleLink: {
		path: string
		title: string
		count?: number
		icon?: any
	}
	links: {
		path: string
		title: string
		count?: number
	}[]
	isVisible: boolean
	setIsVsible: (value: boolean) => void
}

const Dropdown = ({ titleLink, links, isVisible, setIsVsible }: IProps) => {
	const location = useLocation()

	const initialOpen = links.some((i) => location.pathname.includes(i.path))
	const [isOpen, setIsOpen] = useState<boolean>(initialOpen)

	const toggleDropdown = () => {
		if (isOpen) {
			setIsOpen(false)
		} else {
			if (!isVisible) {
				setIsVsible(true)
			}
			setIsOpen(true)
		}
	}

	useEffect(() => {
		if (!isVisible) {
			setIsOpen(false)
		}
	}, [isVisible])

	return (
		<div className={classNames(s.dropdown, { [s.dropdownOpen]: isOpen })}>
			<div className={s.wrapper}>
				<div className={s.dropdownBtn} onClick={toggleDropdown}>
					{titleLink.path ? (
						<NavLink
							to={titleLink.path}
							className={({ isActive }) =>
								classNames(s.navLink, s.dropdownLink, {
									[s.navLinkActive]: isActive,
								})
							}
						>
							<span className={s.navIcon}>
								{titleLink?.icon}
								{titleLink?.count && !isOpen ? <span className={s.navIconCount} /> : null}
							</span>
							{titleLink.title}
						</NavLink>
					) : (
						<span className={classNames(s.navLink, s.dropdownLink)}>
							<span className={s.navIcon}>
								{titleLink?.icon}
								{titleLink?.count && !isOpen ? <span className={s.navIconCount} /> : null}
							</span>
							{titleLink.title}
						</span>
					)}
					<span className={s.dropdownArrow}>
						<ArrowIcon />
					</span>
				</div>
				{titleLink?.count && !isOpen ? <span className={s.count}>{titleLink.count}</span> : null}
			</div>

			{links.map((link) => (
				<div key={link.path} className={classNames(s.wrapper, s.dropdownWrapper)}>
					<NavLink to={link.path} className={({ isActive }) => classNames(s.navLink, { [s.navLinkActive]: isActive })}>
						{link.title}
					</NavLink>
					{link?.count ? <span className={s.count}>{link.count}</span> : null}
				</div>
			))}
		</div>
	)
}

const Aside = () => {
	const { isAuth } = useAppSelector((state) => state.auth)

	const { data } = useGetSidebarQuery({}, { skip: !isAuth })
	const { data: user } = useGetCurrentUserQuery({}, { skip: !isAuth })

	const [isVisible, setIsVisible] = useState<boolean>(true)

	const links = useMemo(
		() =>
			user?.permissions?.length
				? navLinks
						.filter((i) => user.permissions.includes(i.permissionKey))
						.map((i) => ({
							...i,
							...(!!i?.links?.length && {
								links: i.links.filter((j) => user.permissions.includes(j.permissionKey)),
							}),
						}))
						.map((i) => {
							if (i.id === 'clients') {
								return {
									...i,
									count: data?.corporateClients,
									links: i.links?.map((j) => {
										if (j.path === '/corporate-clients') {
											return {
												...j,
												count: data?.corporateClients,
											}
										}
										return j
									}),
								}
							}
							if (i.id === 'trucks') {
								return {
									...i,
									count: (data?.needFillingTrucks || 0) + (data?.needFillingTrailers || 0),
									links: i.links?.map((j) => {
										if (j.path === '/trucks') {
											return {
												...j,
												count: data?.needFillingTrucks,
											}
										}
										if (j.path === '/trailers') {
											return {
												...j,
												count: data?.needFillingTrailers,
											}
										}
										return j
									}),
								}
							}
							if (i.id === 'orders') {
								return {
									...i,
									count: data?.preOrderWithoutDriverCount,
									links: i.links?.map((j) => {
										if (j.path === '/preorders') {
											return {
												...j,
												count: data?.preOrderWithoutDriverCount,
											}
										}
										return j
									}),
								}
							}
							if (i.id === 'notifications') {
								return {
									...i,
									count: data?.notifications.total,
									links: i.links?.map((j) => ({
										...j,
										count: data?.notifications?.[j.path.replace('/', '')],
									})),
								}
							}
							return i
						})
				: [],
		[data, user],
	)

	return (
		<aside className={s.aside}>
			<Hamburger className={s.hamburger} onClick={() => setIsVisible((prev) => !prev)} />

			<nav className={classNames(s.nav, { [s.navHidden]: !isVisible })}>
				{links.map((link) => (
					<React.Fragment key={link.id}>
						{link?.links ? (
							<Dropdown
								titleLink={{
									title: link.title,
									path: link.path,
									count: link?.count,
									icon: link?.icon,
								}}
								links={link.links}
								isVisible={isVisible}
								setIsVsible={setIsVisible}
							/>
						) : (
							<div className={s.wrapper}>
								<NavLink to={link.path} className={({ isActive }) => classNames(s.navLink, { [s.navLinkActive]: isActive })}>
									{link.icon}
									{link.title}
								</NavLink>
								{link?.count ? <span className={s.count}>{link.count}</span> : null}
							</div>
						)}
					</React.Fragment>
				))}
			</nav>
		</aside>
	)
}

const Layout = () => {
	const navigate = useNavigate()

	const [subscribe] = useLazySubscribeToEventsQuery()

	const { setOrderId, setLegalPersonId, setNaturalPersonId, setIsFop, setAuth, setRefuellingsId, setPartiesId } = useActions()

	const { fillObj, truckNeedFilling } = useAppSelector((state) => state.trucks)
	const { isAuth } = useAppSelector((state) => state.auth)
	const { pageTitle, orderIds, legalPersonId, naturalPersonId, isFop, refuellingsId, partiesId } = useAppSelector((state) => state.common)

	const { data: user, isLoading } = useGetCurrentUserQuery({}, { skip: !isAuth })
	const [signOut] = useSignOutMutation()

	const signOutHandler = () => {
		signOut({})
			.unwrap()
			.then(() => {
				setAuth(false)
				navigate('/sign-in')
				localStorage.removeItem('accessToken')
			})
	}

	useEffect(() => {
		if (isAuth) {
			if (!socket) {
				subscribe()
			} else {
				const token = localStorage.getItem('accessToken')
				if (token) {
					/* @ts-ignore-start */
					socket.auth.token = token
					/* @ts-ignore-end */
					socket.connect()
				}
			}
		}
	}, [isAuth])

	return (
		<div className={s.layout}>
			<div className={s.headerWrapper}>
				<Header />
				{isAuth && user && (
					<div className={s.user}>
						<div className={s.userName}>
							{user?.user?.name?.at(0) || ''} {user?.user?.surname?.at(0) || ''}
						</div>
						<div className={s.userDropdown}>
							{user?.user?.name || ''} {user?.user?.surname || ''}
							<Button variant="filled" onClick={signOutHandler}>
								Вийти
							</Button>
						</div>
					</div>
				)}
			</div>

			{isLoading ? (
				<Loader />
			) : (
				<div className={s.mainWrapper}>
					<Aside />

					<main className={s.mainContent}>
						<h1 className={s.pageTitle}>{pageTitle}</h1>

						<Outlet />
					</main>

					{truckNeedFilling?.tankId && user?.permissions?.includes('REFUELLINGS_CREATE') ? <TrucksNotificationModal data={truckNeedFilling} /> : null}
					{fillObj?.id ? <TrucksFillModal id={fillObj.id} type={fillObj.type} /> : null}
					{orderIds?.length 
						? orderIds.map((orderId) => (
							<OrdersDetailModal 
								key={orderId}
								id={orderId} 
								closeHandler={() => setOrderId(orderId)} 
							/> 
						))
						: null
					}
					{legalPersonId ? (
						<LegalPersonsDetailModal
							id={legalPersonId}
							closeHandler={() => {
								setLegalPersonId('')
								setIsFop(false)
							}}
							isFop={isFop}
						/>
					) : null}
					{naturalPersonId ? <NaturalPersonsDetailModal id={naturalPersonId} closeHandler={() => setNaturalPersonId('')} /> : null}
					{refuellingsId ? <RefuellingsDetailModal id={refuellingsId} closeHandler={() => setRefuellingsId('')} /> : null}
					{partiesId ? <EditPartiesModal id={partiesId} closeHandler={() => setPartiesId('')} /> : null}			
				</div>
			)}
		</div>
	)
}

export default Layout
